import * as React from 'react';
import styled, { css } from 'styled-components';
import { em, rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n/lib';
import API, { ThenArg } from '../../../services/API';
import { inCart, isTablet } from '../../../utilities';
import { redirectToCart } from '../../../containers/Cart/actions';
import HeaderNew from './HeaderNew/HeaderNew';
import HeaderMobile from './HeaderMobile/HeaderMobile';
import { Media, MediaContextProvider } from '../../../theme/media';
import OverlaySearch from '../../_helpers/Overlay/OverlaySearch';
import { push } from 'react-router-redux';
import { audienceFromQuery } from './HeaderNew/menuData';

const Wrapper = styled.header`
  background: ${({ theme }) => theme.color.primary};
  width: 100%;
  top: 0;
  left: 0;
  position: fixed;
  z-index: ${(props) => (rem(props.searchDropdownVisible) ? 101 : 11)};
  @media (max-width: ${em(991)}) {
    max-height: 128px;
  }
  ${({ theme }) => theme.transition('transform')};
  ${({ visible }) => {
    if (visible) {
      return css`
        transform: translate3d(0, -100%, 0);
      `;
    }
  }}
  ${({ theme }) => theme.media('md')} {
    /* Above the sticky left side-menu (z-index: 40 in grid.tsx): when that menu
       is taller than the viewport it scrolls up past the fixed header — it
       should slide *under* the header, not paint over it. Kept below the
       cart/overlays/modals (51, 100+) so those still sit on top. */
    z-index: 45;
    background: ${({ theme }) => theme.color.white};
    /* Always fixed on desktop (inherited from the base rule) — the header must
       stay visible when scrolling even on short viewports; body compensates
       with padding-top: var(--header-height) (globalStyles). The mobile
       hide-on-scroll transform is undone here. */
    ${({ visible }) => {
      if (visible) {
        return css`
          transform: translate3d(0, 0, 0);
        `;
      }
    }};
    &.cart {
      margin-bottom: 0;
    }
  }

  @media print {
    position: relative;
  }
`;

export const MenuIconStyles = css`
  display: inline-block;
  background: ${({ theme }) => theme.color.white};
  border-radius: ${rem(2)};
  ${({ theme }) => theme.size(30, 3)};
  vertical-align: middle;
  position: relative;
  transform: translate3d(0, -100%, 0);
  ${({ theme }) => theme.transition('background-color')};
  &:before,
  &:after {
    display: block;
    content: '';
    background: ${({ theme }) => theme.color.white};
    border-radius: ${rem(2)};
    ${({ theme }) => theme.size(30, 3)};
    position: absolute;
    left: 0;
    ${({ theme }) => theme.transition('transform')};
  }
  &:before {
    bottom: ${rem(10)};
  }
  &:after {
    top: ${rem(10)};
  }
`;

export const MenuIconActiveStyles = css`
  background: transparent;
  &:before {
    transform: translateY(${rem(10)}) rotate(-45deg);
  }
  &:after {
    transform: translateY(${rem(-10)}) rotate(45deg);
  }
`;

interface HeaderProps {
  visible: boolean;
  mainMenu: any[];
  dispatch: any;
  cart: ThenArg<typeof API.getCart>;
  cartError?: any;
  user: any;
  searchDropdownVisible: boolean;
  searchDropdownMobileVisible: boolean;
  searchedQuery: string;
  searchedResults: any[];
  searchedResultsLoader: boolean;
  onChangeSearchInput: (q: string) => void;
  onSubmitSearchInput: (section: string) => void;
  onAppendCategories: () => void;
  onAppendProducts: () => void;
  onAppendWebContents: () => void;
  onClickOutsideSearchForm: () => void;
  onClickOutsideSearchMobileForm: () => void;
  onRedirect: (url) => void;
  onSearchFocus: (isFocus: boolean) => void;
  currentPath: string;
  categoriesGroup: any[];
  favoriteProductsCount: number;
  currentUniqs: string[];
  currentCategoryGroupId?: number;
  currentCategoryId?: number;
  location: any;
  settings: any;
  singleSchoolArticle: any;
  deliveryFreePrice: number;
}

interface HeaderStates {
  minicartVisible: boolean;
  mobileNavVisible: boolean;
  userMetaVisible: boolean;
  searchVisible: boolean;
}

class Header extends React.PureComponent<HeaderProps, HeaderStates> {
  constructor(props) {
    super(props);

    this.state = {
      minicartVisible: false,
      mobileNavVisible: false,
      userMetaVisible: false,
      searchVisible: false,
    };
  }

  public redirectToCart = () => {
    this.props.dispatch(redirectToCart());
  };

  public onClickFavorite = () => {
    this.props.dispatch(push('/my-account/favorite-products'));
  };

  handleURLChange = () => {
    this.setState({
      mobileNavVisible: false,
    });
  };

  // Check for URL changes in componentDidUpdate
  componentDidUpdate(prevProps) {
    if (this.props.location !== prevProps.location) {
      this.handleURLChange();
    }
  }

  public render() {
    const user = this.props.user;

    const { userMetaVisible } = this.state;
    const {
      dispatch,
      cart,
      visible,
      categoriesGroup,
      mainMenu,
      currentPath,
      searchDropdownVisible,
      searchedQuery,
      searchedResults,
      searchedResultsLoader,
      onChangeSearchInput,
      onClickOutsideSearchForm,
      onRedirect,
      onSubmitSearchInput,
      onAppendCategories,
      onAppendProducts,
      onAppendWebContents,
      onSearchFocus,
      currentUniqs,
      currentCategoryGroupId,
      currentCategoryId,
      settings,
    } = this.props;

    const tablet = isTablet();

    const queryAudience = audienceFromQuery(this.props.location?.query);

    if (visible && tablet) {
      this.setAllBooleanStatesFalse();
    }

    return (
      <MediaContextProvider>
        {searchDropdownVisible && tablet && <OverlaySearch />}
        <Wrapper
          searchDropdownVisible={searchDropdownVisible}
          visible={visible}
          userMetaVisible={userMetaVisible}
          className={inCart(currentPath) ? 'cart' : ''}
        >
          <Media greaterThanOrEqual="lg">
            <HeaderNew
              mainMenu={mainMenu}
              user={user}
              cart={cart}
              cartError={this.props.cartError}
              deliveryFreePrice={this.props.deliveryFreePrice}
              settings={settings}
              dispatch={dispatch}
              currentPath={currentPath}
              currentUniqs={currentUniqs}
              currentCategoryGroupId={currentCategoryGroupId}
              currentCategoryId={currentCategoryId}
              categoriesGroup={categoriesGroup}
              favoriteProductsCount={this.props.favoriteProductsCount}
              queryAudience={queryAudience}
              searchDropdownVisible={searchDropdownVisible}
              searchedQuery={searchedQuery}
              searchedResults={searchedResults}
              searchedResultsLoader={searchedResultsLoader}
              onChangeSearchInput={onChangeSearchInput}
              onClickOutsideSearchForm={onClickOutsideSearchForm}
              onRedirect={onRedirect}
              onSubmitSearchInput={onSubmitSearchInput}
              onAppendCategories={onAppendCategories}
              onAppendProducts={onAppendProducts}
              onAppendWebContents={onAppendWebContents}
              onSearchFocus={onSearchFocus}
            />
          </Media>
          <Media lessThan="lg">
            <HeaderMobile
              mainMenu={mainMenu}
              user={user}
              cart={cart}
              cartError={this.props.cartError}
              deliveryFreePrice={this.props.deliveryFreePrice}
              settings={settings}
              dispatch={dispatch}
              currentPath={currentPath}
              currentUniqs={currentUniqs}
              currentCategoryGroupId={currentCategoryGroupId}
              currentCategoryId={currentCategoryId}
              categoriesGroup={categoriesGroup}
              favoriteProductsCount={this.props.favoriteProductsCount}
              queryAudience={queryAudience}
              searchDropdownVisible={!visible && searchDropdownVisible}
              searchedQuery={searchedQuery}
              searchedResults={searchedResults}
              searchedResultsLoader={searchedResultsLoader}
              onChangeSearchInput={onChangeSearchInput}
              onClickOutsideSearchForm={onClickOutsideSearchForm}
              onRedirect={onRedirect}
              onSubmitSearchInput={onSubmitSearchInput}
              onAppendCategories={onAppendCategories}
              onAppendProducts={onAppendProducts}
              onAppendWebContents={onAppendWebContents}
              onSearchFocus={onSearchFocus}
            />
          </Media>
        </Wrapper>
      </MediaContextProvider>
    );
  }

  public toggleMobileNav = () => {
    this.setAllBooleanStatesFalse();
    this.setState({
      mobileNavVisible: !this.state.mobileNavVisible,
    });
  };

  private setAllBooleanStatesFalse = (
    userMetaVisible = true,
    minicartVisible = true,
  ) => {
    if (this.state.minicartVisible && minicartVisible) {
      this.setState({
        minicartVisible: false,
      });
    }
    if (this.state.userMetaVisible && userMetaVisible) {
      this.setState({
        userMetaVisible: false,
      });
    }
    if (this.state.mobileNavVisible) {
      this.setState({
        mobileNavVisible: false,
      });
    }
  };
}

export default Header;
